/* eslint-disable no-underscore-dangle */
import _ from 'lodash';
import { usePathname } from 'next/navigation';
import PropTypes from 'prop-types';
import {
  KetoProductCardImpression,
  PREHEADER_TYPE,
} from '@nerdwallet/react-marketplace-product-card';
import classNames from 'classnames';
import { DISPLAY_PAGES } from '@nerdwallet/react-marketplace-apply-now';
import appConfig from '@nerdwallet/app-config';
import { useEffect, useRef, useState } from 'react';
import { useIdentity } from '~/app/lib/redux/selectors/identity-selector';
import SegmentImpression from '../segment-impression';
import WhyThisProduct from './WhyThisProduct';

import styles from './keto-product-card.module.scss';
import { PRODUCT_TYPE_UNSTRUCTURED_VERTICAL } from './utils';
import WhyNerdsLoveIt from './WhyNerdsLoveIt';

const KetoUnstructuredCard = ({
  keyFacts,
  productCardImage,
  heading,
  analytics,
  ctaHref,
  ctaText,
  segmentImpressionId,
  pageViewId,
  identifier,
  ctaSubtext,
  omitRating,
  omittedTabs,
  productType,
  starRating,
  badgeProps,
  shouldOutputCtaBelowKeyFacts,
  footerOptions,
}) => {
  const pathname = usePathname();
  const identity = useIdentity();

  const {
    includeWhyThisProduct,
    includeWhyNerdsLoveIt,
    whyThisProductMethodologyUrl,
    whyThisProductText,
    whyThisProductTypeOverride,
    whyNerdsLoveItContainer,
    whyNerdsLoveItTitle,
    whyNerdsLoveItTitleIconUrl,
    whyNerdsLoveItText,
    whyNerdsLoveItMethodologyUrl,
    whyNerdsLoveItAvatarUrl,
    turnOnGroupedBorders,
  } = footerOptions;

  const productImage = productCardImage && {
    src: productCardImage,
    alt: heading,
  };

  const keyFactsMap = keyFacts?.map((item) => ({
    __typename: 'EntityPlacementDriver',
    description: null,
    label: item?.label,
    labelTooltip: null,
    tooltip: null,
    value: item?.text,
    valueTooltip: item?.tooltip,
    valueTooltipHasHtml: false,
  }));

  const unstructuredCardProps = {
    wrapDrivers: true,
    drivers: keyFactsMap,
  };

  const baseCard = {
    status: 'ACTIVE',
    entity_type: 'offers',
    product_type: productType,
    product: {
      status: 'ACTIVE',
    },
  };

  const segmentImpressionProps = {
    impression_id: segmentImpressionId,
    deployable_name: appConfig?._meta?.deployable,
    deployable_version: appConfig?._meta?.version,
    sku_level: baseCard.entity_type,
    sku_name: heading,
    sku_product_type: productType,
    sku_source: 'marketplace',
    impression_page_url: pathname,
    impression_pageview_id: pageViewId,
    sku_impression_id: segmentImpressionId,
  };

  const [applyButtonComponent, setApplyButtonComponent] = useState(null);
  const ketoContainerRef = useRef();
  const originalCtaParentElementRef = useRef(null);
  const applyButtonNode =
    ketoContainerRef.current?.getElementsByClassName('apply-now')?.[0]
      ?.parentNode;

  useEffect(() => {
    // In order to capture ApplyButton in Mobile Viewports at the first render
    // we need to set it into a state
    setApplyButtonComponent(applyButtonNode);
  }, [applyButtonNode]);

  useEffect(() => {
    // Workaround to move CTA button to below Key Facts no touching react-components
    if (shouldOutputCtaBelowKeyFacts && ketoContainerRef.current) {
      const desiredParentElement =
        ketoContainerRef.current.children[0].children[0].children[1].children[1]
          .children[0];

      if (applyButtonComponent && desiredParentElement) {
        originalCtaParentElementRef.current = applyButtonComponent.parentNode;
        desiredParentElement.appendChild(applyButtonComponent);
        applyButtonComponent.style.marginBottom = '16px';
      }
    }
  }, [applyButtonComponent, shouldOutputCtaBelowKeyFacts]);

  useEffect(() => {
    // Only for user's visual feedback, once the original layout will be outputted as default
    const shouldReverseToInitialLayout =
      !shouldOutputCtaBelowKeyFacts &&
      originalCtaParentElementRef.current &&
      applyButtonComponent;

    if (shouldReverseToInitialLayout) {
      originalCtaParentElementRef.current.appendChild(applyButtonComponent);
    }
  }, [
    applyButtonComponent,
    originalCtaParentElementRef,
    shouldOutputCtaBelowKeyFacts,
  ]);

  const badge = {
    hasPreheader: true,
    preheaderText: badgeProps?.badgeText,
    preheaderType: PREHEADER_TYPE.BADGE_2025,
  };

  return (
    <SegmentImpression
      eventName="SKU Viewed"
      eventProps={segmentImpressionProps}
    >
      <div
        id={identifier}
        className={classNames(styles.ketoProductCard)}
        ref={ketoContainerRef}
      >
        <KetoProductCardImpression
          className={classNames([
            styles.unstructuredCard,
            { [styles.disableKetoBottomBorder]: turnOnGroupedBorders },
          ])}
          analytics={analytics}
          identifier={identifier}
          ctaHref={ctaHref}
          ctaSubtext={ctaSubtext}
          ctaText={ctaText}
          displayPage={DISPLAY_PAGES.DIGITAL_MARKETING}
          hasAccordionOnMobile
          heading={heading}
          identity={identity}
          imageData={productImage}
          marketplaceEntity={baseCard}
          omitRating={omitRating}
          omittedTabs={omittedTabs}
          productType={productType}
          shouldLogImpression={false}
          starRating={_.toNumber(starRating)}
          {...badge}
          {...unstructuredCardProps}
          preheader={{
            text: 'custom text',
            type: 'BADGE_2025',
          }}
        />
        {includeWhyThisProduct && (
          <WhyThisProduct
            whyThisProductText={whyThisProductText}
            whyThisProductTypeOverride={whyThisProductTypeOverride}
            whyThisProductMethodologyUrl={whyThisProductMethodologyUrl}
          />
        )}
        {includeWhyNerdsLoveIt && (
          <WhyNerdsLoveIt
            container={whyNerdsLoveItContainer}
            whyNerdsLoveItMethodologyUrl={whyNerdsLoveItMethodologyUrl}
            turnOnGroupedBorders={turnOnGroupedBorders}
            whyNerdsLoveItText={whyNerdsLoveItText}
            whyNerdsLoveItTitle={whyNerdsLoveItTitle}
            whyNerdsLoveItTitleIconUrl={whyNerdsLoveItTitleIconUrl}
            whyNerdsLoveItAvatarUrl={whyNerdsLoveItAvatarUrl}
          />
        )}
      </div>
    </SegmentImpression>
  );
};

KetoUnstructuredCard.propTypes = {
  keyFacts: PropTypes.arrayOf(PropTypes.string),
  productCardImage: PropTypes.string,
  cardIdentifier: PropTypes.object,
  heading: PropTypes.string,
  badgeText: PropTypes.string,
  ctaHref: PropTypes.string,
  ctaSubtext: PropTypes.string,
  ctaText: PropTypes.string,
  segmentImpressionId: PropTypes.string,
  pageViewId: PropTypes.string,
  identifier: PropTypes.string,
  omitRating: PropTypes.bool,
  omittedTabs: PropTypes.bool,
  productType: PropTypes.oneOf(_.values(PRODUCT_TYPE_UNSTRUCTURED_VERTICAL)),
  starRating: PropTypes.string,
  includeWhyThisProduct: PropTypes.bool,
  includeWhyNerdsLoveIt: PropTypes.bool,
  renderWhyThisProduct: PropTypes.func,
  renderWhyNerdsLoveIt: PropTypes.func,
  badgeProps: PropTypes.object,
  analytics: PropTypes.object,
  turnOnGroupedBorders: PropTypes.bool,
  shouldOutputCtaBelowKeyFacts: PropTypes.bool,
  footerOptions: PropTypes.shape({
    whyThisProductMethodologyUrl: PropTypes.string,
    whyThisProductText: PropTypes.string,
    whyThisProductTypeOverride: PropTypes.string,
    whyNerdsLoveItContainer: PropTypes.string,
    whyNerdsLoveItTitle: PropTypes.string,
    whyNerdsLoveItTitleIconUrl: PropTypes.string,
    whyNerdsLoveItText: PropTypes.string,
    whyNerdsLoveItMethodologyUrl: PropTypes.string,
    includeWhyThisProduct: PropTypes.bool,
    includeWhyNerdsLoveIt: PropTypes.bool,
    turnOnGroupedBorders: PropTypes.bool,
    whyNerdsLoveItAvatarUrl: PropTypes.string,
  }),
};

export default KetoUnstructuredCard;
